/**
 * ClikArt Service
 *
 * GET /clikart/search?displayName=&keywordIds= — searches the ClikArt library across all
 *   categories via the Image Service API. Both params are optional; keywordIds is a
 *   comma-separated list of integer ids and combines with AND upstream. Confirmed live
 *   (2026-09-29): omitting both returns an EMPTY payload — upstream requires an actual
 *   constraint, it is not a "give me everything" mode. Response payload groups matches by
 *   category (same shape as /clikart/categories) — flattened here since this panel only needs a
 *   flat gallery; Category selection stays a client-side filter over the result.
 *
 * GET /clikart/keywords — retrieves the ClikArt CMS keyword list. No parameters; small, fetched
 *   once. Selected keyword names are resolved to ids client-side before searching (see
 *   resolve-clikart-keyword-ids.ts).
 *
 * GET /clikart/categories?page=&size= — the whole-library dump (~21MB / ~12,900 items / ~66s
 *   upstream), now paged by category group and cached server-side for 24h. The first request
 *   warms that cache, so the editor sends it early (see `useClikArtCategoriesWarmup`) and this
 *   service walks the pages until the server says there is no more (`hasMore: false`, or a short
 *   page when the response has no such flag). The response is the paged object
 *   `{ categories, page, size, totalCategories, totalPages, hasMore }`; the `{ result, payload }`
 *   envelope and a bare array are still accepted. Since /clikart/search has no
 *   unfiltered mode, this is the only endpoint that can power the gallery's default view (nothing
 *   typed/selected) — confirmed with backend. `useClikArtCategoriesQuery` caches this for the
 *   whole session so it only fires once; the panel additionally windows the (potentially huge)
 *   result client-side (`useTrayLazyWindow`) rather than rendering every item at once.
 *
 * GET /clikart/placedIds and POST /clikart/scanPlacedIds — which ClikArt is placed in the
 *   session's book, per spread, from the backend's cache (see `getPlacedClikArtIds`).
 */

import { httpClient } from '@/api/http/client'
import type {
  ClikArtByIdResponse,
  ClikArtCategory,
  ClikArtCmsKeyword,
  ClikArtItem,
  ClikArtKeywordsResponse,
  ClikArtSearchResponse,
} from '@/api/types/clikart'
import {
  extractClikArtCategories,
  readClikArtCategoriesHasMore,
} from '@/lib/clikart/extract-clikart-categories'
import { flattenClikArtCategories } from '@/lib/clikart/flatten-clikart-categories'
import {
  type ClikArtPlacedIds,
  normalizeClikArtPlacedIds,
} from '@/lib/clikart/normalize-clikart-placed-ids'

// The categories endpoint pages by category group (not by item); the backend caches the whole
// library for 24h, so every page after the first is a cheap slice. MAX_PAGES is only a runaway
// guard — the loop normally ends on the first short page.
const CATEGORIES_PAGE_SIZE = 100
const CATEGORIES_MAX_PAGES = 50

export interface SearchClikArtParams {
  displayName?: string
  keywordIds?: number[]
}

function buildSearchPath({ displayName, keywordIds }: SearchClikArtParams): string {
  const params = new URLSearchParams()
  const trimmedName = displayName?.trim()

  if (trimmedName) {
    params.set('displayName', trimmedName)
  }

  if (keywordIds && keywordIds.length > 0) {
    params.set('keywordIds', keywordIds.join(','))
  }

  const query = params.toString()

  return query ? `/clikart/search?${query}` : '/clikart/search'
}

async function searchClikArt(params: SearchClikArtParams): Promise<ClikArtItem[]> {
  const { result, payload } = await httpClient.get<ClikArtSearchResponse>(buildSearchPath(params))

  if (!result || !Array.isArray(payload)) {
    throw new Error('Failed to search ClikArt')
  }

  return flattenClikArtCategories(payload)
}

async function getClikArtKeywords(): Promise<ClikArtCmsKeyword[]> {
  const { result, payload } = await httpClient.get<ClikArtKeywordsResponse>('/clikart/keywords')

  if (!result || !Array.isArray(payload)) {
    throw new Error('Failed to load ClikArt keywords')
  }

  return payload
}

async function getClikArtCategories(): Promise<ClikArtItem[]> {
  const categories: ClikArtCategory[] = []
  const seenCategoryIds = new Set<number>()

  for (let page = 1; page <= CATEGORIES_MAX_PAGES; page += 1) {
    const response = await httpClient.get<unknown>(
      `/clikart/categories?page=${page}&size=${CATEGORIES_PAGE_SIZE}`
    )
    const pageCategories = extractClikArtCategories(response)

    if (!pageCategories) {
      throw new Error('Failed to load ClikArt categories')
    }

    // A backend that ignores paging returns the full library for every page; stop as soon as a
    // page adds nothing new instead of looping on duplicates.
    const newCategories = pageCategories.filter((c) => !seenCategoryIds.has(c.artCategoryId))

    newCategories.forEach((c) => seenCategoryIds.add(c.artCategoryId))
    categories.push(...newCategories)

    // The paged response says whether another page follows (`hasMore`); a bare array or envelope
    // doesn't, so a short page ends the walk there. A page with nothing new always ends it.
    const hasMore = readClikArtCategoriesHasMore(response)
    const lastPage = hasMore === undefined ? pageCategories.length < CATEGORIES_PAGE_SIZE : !hasMore

    if (lastPage || newCategories.length === 0) {
      break
    }
  }

  return flattenClikArtCategories(categories)
}

/**
 * GET /art/{artId} — one ClikArt by id, or null when the id is unknown. The only way to get an
 * item's `scrUrl` without the whole library: a ClikArt that arrives as an id alone (a template's,
 * or one authored in the legacy app) has no preview URL until this resolves it.
 */
async function getClikArtById(artId: string): Promise<ClikArtItem | null> {
  const { result, payload } = await httpClient.get<ClikArtByIdResponse>(
    `/art/${encodeURIComponent(artId)}`
  )

  if (!result || !Array.isArray(payload)) {
    throw new Error(`Failed to load ClikArt ${artId}`)
  }

  return payload.find((item) => item.artId === artId && item.scrUrl) ?? null
}

/**
 * GET /clikart/placedIds — the book's placed ClikArt from the backend's cache (the book is the
 * session's job): each spreadId mapped to its ClikArt ids, with the state of the scan that fills
 * it. Never starts a scan, so it is cheap to read repeatedly. See `normalizeClikArtPlacedIds`.
 */
async function getPlacedClikArtIds(): Promise<ClikArtPlacedIds> {
  const placedIds = normalizeClikArtPlacedIds(await httpClient.get<unknown>('/clikart/placedIds'))

  if (!placedIds) {
    throw new Error('Failed to load placed ClikArt')
  }

  return placedIds
}

/**
 * POST /clikart/scanPlacedIds — returns the same as `getPlacedClikArtIds` and, when the last scan
 * is older than an hour and none is running, starts a background scan of every spread that has a
 * checkpoint. Returns right away; the result then reads as scanning and the ids fill in later.
 * Calling it again while a scan runs does not start another.
 */
async function scanPlacedClikArtIds(): Promise<ClikArtPlacedIds> {
  const placedIds = normalizeClikArtPlacedIds(
    await httpClient.post<undefined, unknown>('/clikart/scanPlacedIds')
  )

  if (!placedIds) {
    throw new Error('Failed to scan for placed ClikArt')
  }

  return placedIds
}

export const clikArtService = {
  searchClikArt,
  getClikArtById,
  getClikArtKeywords,
  getClikArtCategories,
  getPlacedClikArtIds,
  scanPlacedClikArtIds,
}
